// src/components/editor/EditorRightRail.jsx
//
// Right-hand rail for the manuscript editor (mock revisionari_writing_page (7)).
// Rendered as a sibling flex column of the main editor content — NOT nested in
// .editor-main-content — so it never perturbs that column's centering/overflow
// math, which the rulers/zoom depend on.
//
// Skinned in the app's existing purple/gold system (mocks are cream, but the
// ticket keeps universal styling the same — mocks are treated as layout only).
//
// The rail swaps its contents for a live paginated preview of the manuscript
// (EditorRailPreview) and back to Chapter progress / Theme / Reviewer activity.
// The rail itself stays put either way — only what's inside it changes. The
// toggle that drives this lives in the editor's doc bar, not in here, so it is
// reachable whatever the rail is currently showing; `onPreview` is still passed
// down because the preview's own close control uses it. The Theme box is the
// manuscript's theme (ThemeRailCard). Chapter progress and Reviewer activity
// are wired to real editor state.
import React from 'react';
import { Box, Typography, Avatar, AvatarGroup } from '@mui/material';
import EditorRailPreview from './EditorRailPreview';
import ThemeRailCard from '../manuscriptTheme/ThemeRailCard';

const GOLD = '#D4AF37';
const GOLD_HOVER = '#F5D76E';

// Deterministic 2-letter initials for a reviewer avatar.
const initialsOf = (u) => {
  const name = u?.username || u?.name || u?.email || '?';
  const parts = String(name).trim().split(/\s+/);
  const a = parts[0]?.[0] || '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0]?.[1] || '');
  return (a + b).toUpperCase();
};

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mb: 1 }}>
    {children}
  </Typography>
);

const EditorRightRail = ({
  currentWords = 0,
  wordGoal = 2000,
  // The manuscript theme — the look of the author's book, not the app's.
  theme = null,
  themeSample = null,
  trimLabel = '',
  canChangeTheme = false,
  onCustomizeTheme,
  themeOverrideCount = 0,
  onResetToTheme,
  reviewers = [],
  pendingSuggestions = 0,
  onPreview,
  previewOpen = false,
  previewHtml = '',
  previewFormat = 'book',
  onPreviewFormatChange,
  previewScope = 'chapter',
  onPreviewScopeChange,
  trimSize,
  previewChapterRange = null,
  open = false,
  // While the Goal tab is open: today's words and the upcoming deadlines, in
  // place of the theme and reviewer sections.
  goalsSection = null,
}) => {
  const goal = wordGoal > 0 ? wordGoal : 2000;
  const pct = Math.max(0, Math.min(100, Math.round((currentWords / goal) * 100)));

  return (
    // is-rail-open marks "the rail is showing something the user asked for".
    // Below 900px the rail stops taking width and slides in over the canvas
    // instead, so it needs to know when it has been summoned rather than
    // sitting there by default. Above that it stays docked and the class is
    // only along for the ride. `open` covers both routes in: the canvas-edge
    // handle, and opening the preview.
    <Box className={`editor-right-rail${open ? ' is-rail-open' : ''}${previewOpen ? ' is-preview-open' : ''}`}>
      {previewOpen ? (
        <EditorRailPreview
          html={previewHtml}
          format={previewFormat}
          onFormatChange={onPreviewFormatChange}
          scope={previewScope}
          onScopeChange={onPreviewScopeChange}
          trimSize={trimSize}
          chapterRange={previewChapterRange}
          onClose={onPreview}
        />
      ) : (
        <>
        <SectionLabel>Chapter progress</SectionLabel>
        <Box sx={{ height: 7, borderRadius: '4px', background: 'rgba(255,255,255,0.15)', mb: 0.75, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${pct}%`, background: GOLD, borderRadius: '4px', transition: 'width 0.25s ease' }} />
        </Box>
        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mb: 2.75 }}>
          {currentWords.toLocaleString()} of {goal.toLocaleString()} word goal
        </Typography>

        {goalsSection || (<>
        <SectionLabel>Theme</SectionLabel>
        <ThemeRailCard
          theme={theme}
          sample={themeSample}
          trimLabel={trimLabel}
          canChange={canChangeTheme}
          onCustomize={onCustomizeTheme}
          overrideCount={themeOverrideCount}
          onReset={onResetToTheme}
        />

        <SectionLabel>Reviewer activity</SectionLabel>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {reviewers.length > 0 ? (
            <AvatarGroup
              max={4}
              sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 10, fontWeight: 700, bgcolor: '#6B4FA0', border: '1px solid #4A3F8C' } }}
            >
              {reviewers.map((r, i) => (
                <Avatar key={r._id || r.id || i}>{initialsOf(r)}</Avatar>
              ))}
            </AvatarGroup>
          ) : (
            <Avatar sx={{ width: 24, height: 24, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.15)' }}> </Avatar>
          )}
          <Typography sx={{ fontSize: 12, color: '#fff' }}>
            {pendingSuggestions > 0
              ? `${pendingSuggestions} ${pendingSuggestions === 1 ? 'suggestion' : 'suggestions'} pending`
              : 'No suggestions pending'}
          </Typography>
        </Box>
        </>)}
        </>
      )}
    </Box>
  );
};

export { GOLD, GOLD_HOVER };
export default EditorRightRail;
